class ScrollVideo {
    /**
     * Create a new scroll-triggered video effect with smoother playback
     * 
     * @param {Object} options - Configuration options
     * @param {string} options.videoId - ID of the video element
     * @param {string} options.wrapperId - ID of the wrapper element containing the video
     * @param {string} options.sectionId - ID of the section containing the scroll area
     * @param {number} options.startWidth - Starting width in pixels
     * @param {number} options.endWidth - Ending width in pixels (or 'fullscreen' for window width)
     * @param {boolean} options.growOnScroll - If true, video grows on scroll; if false, it shrinks
     * @param {boolean} options.reversePlayback - If true, video plays in reverse as user scrolls
     * @param {number} options.extraWidthOffset - Additional width to add (or subtract) from the final width
     * @param {boolean} options.useEasing - If true, applies easing to the size animation (squared progression)
     * @param {number} options.scrollThrottle - Milliseconds to throttle scroll events (default: 16ms for ~60fps)
     * @param {boolean} options.waitForFullLoad - If true, waits for video to be fully loaded before initializing
     */
    constructor(options) {
      this.options = {
        videoId: 'scroll-video',
        wrapperId: 'video-wrapper',
        sectionId: 'video-section',
        startWidth: 400,
        endWidth: 'fullscreen',
        growOnScroll: true,
        reversePlayback: false,
        extraWidthOffset: 0,
        useEasing: false,
        scrollThrottle: 16, // ~60fps
        waitForFullLoad: true,
        ...options
      };
  
      this.video = document.getElementById(this.options.videoId);
      this.wrapper = document.getElementById(this.options.wrapperId);
      this.section = document.getElementById(this.options.sectionId);
      
      if (!this.video || !this.wrapper || !this.section) {
        console.error('ScrollVideo: Required elements not found');
        return;
      }
      
      this.lastScrollTime = 0;
      this.ticking = false;
      this.videoReady = false;
      this.lastProgress = 0;
      
      this.init();
    }
  
    init() {
      this.video.preload = 'auto';
      
      // For small videos, we may want to ensure they're fully loaded
      if (this.options.waitForFullLoad) {
        this.video.addEventListener('canplaythrough', this.handleVideoReady.bind(this), { once: true });
        
        // Fallback if the video takes too long to load fully
        setTimeout(() => {
          if (!this.videoReady) {
            this.handleVideoReady();
          }
        }, 5000);
      } else {
        this.video.addEventListener('loadedmetadata', this.handleMetadataLoaded.bind(this));
        this.video.load();
      }
      
      this.aspectRatio = 16/9;
      this.setInitialSize();

      // Only set up initial event listeners - we'll add scroll listener after video is ready
      window.addEventListener('resize', this.handleResize.bind(this));
    }
    
    handleVideoReady() {
      this.videoReady = true;
      this.handleMetadataLoaded();
      this.setupScrollListener();
      
      // Create a loading indicator message and remove it when ready
      const loadingIndicator = document.getElementById('video-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
      
      console.log('Video ready for scroll interaction');
    }
  
    setupScrollListener() {
      window.addEventListener('scroll', this.handleScrollThrottled.bind(this), { passive: true });
      window.dispatchEvent(new Event('scroll'));
    }
  
    handleMetadataLoaded() {
      if (this.video.videoWidth && this.video.videoHeight) {
        this.aspectRatio = this.video.videoWidth / this.video.videoHeight;
        this.wrapper.style.aspectRatio = `${this.video.videoWidth}/${this.video.videoHeight}`;
        this.setInitialSize();
      }
    }
    
    setInitialSize() {
      const initialWidth = this.options.growOnScroll
        ? this.getStartWidth()
        : this.getEndWidth();
        
      this.wrapper.style.width = `${initialWidth}px`;
      this.options.reversePlayback && (this.video.currentTime = this.video.duration)
    }
  
    getEndWidth() {
      if (this.options.endWidth === 'fullscreen') {
        return window.innerWidth + this.options.extraWidthOffset;
      }
      return this.options.endWidth;
    }
    
    getStartWidth() {
      if (this.options.startWidth === 'fullscreen') {
        return window.innerWidth + this.options.extraWidthOffset;
      }
      return this.options.startWidth;
    }
    
    handleResize() {
      this.setInitialSize();
      window.dispatchEvent(new Event('scroll'));
    }
    
    handleScrollThrottled() {
      const now = Date.now();
      
      // Throttle scroll events
      if (now - this.lastScrollTime < this.options.scrollThrottle) {
        if (!this.ticking) {
          this.ticking = true;
          requestAnimationFrame(() => {
            this.handleScroll();
            this.ticking = false;
          });
        }
        return;
      }
      
      this.lastScrollTime = now;
      this.handleScroll();
    }
    
    handleScroll() {
      if (!this.videoReady) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      const sectionStart = this.section.offsetTop;
      const sectionEnd = sectionStart + this.section.offsetHeight - window.innerHeight;
      
      let scrollProgress = (scrollTop - sectionStart) / (sectionEnd - sectionStart);
      scrollProgress = Math.max(0, Math.min(1, scrollProgress));
      
      // Only update if the progress has changed meaningfully
      // This helps reduce unnecessary updates
      if (Math.abs(scrollProgress - this.lastProgress) > 0.001) {
        this.updateSize(scrollProgress);
        this.updatePlayback(scrollProgress);
        this.updateOverlay(scrollProgress)
        this.lastProgress = scrollProgress;
      }
    }
    
    updateSize(progress) {
      const startWidth = this.getStartWidth();
      const endWidth = this.getEndWidth();
      
      const easedProgress = this.options.useEasing ? progress * progress : progress;
      
      let currentWidth;
      if (this.options.growOnScroll) {
        currentWidth = startWidth + (easedProgress * (endWidth - startWidth));
      } else {
        currentWidth = endWidth - (easedProgress * (endWidth - startWidth));
      }
      
      this.wrapper.style.width = `${currentWidth}px`;
    }
    
    updatePlayback(progress) {
    if (!this.video.duration) return;

    const targetTime = this.options.reversePlayback
        ? this.video.duration - (this.video.duration * progress)
        : this.video.duration * progress;

    if (this.currentTween) {
        this.currentTween.kill(); // Stop any ongoing tween to avoid conflicts
    }

    this.currentTween = gsap.to(this.video, {
        currentTime: targetTime,
        duration: 0.05, // Adjust for smoother transitions
        ease: "power1.in", // Smooth easing for natural effect
    });
  }
  updateOverlay(progress){
    if (progress > 0.6) {
        this.wrapper.style.opacity = 1 - ((progress - 0.6) / 0.4);
      this.section.background = "black"
    } else {
        this.wrapper.style.opacity = 1;
    }
  }

}